import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient, buildQueryParams } from "@finitless/shared/lib";
import { useApiStore } from "@finitless/shared/stores";
import { toast } from "sonner";

/**
 * Hook to get driver companies (delivery services)
 * @param {Object} params - Query parameters
 * @returns {Object} Query result with driver companies
 */
export const useDriverCompanies = (params = {}) => {
  // Build where clause for system-owned driver companies
  const queryParams = {
    ...params,
    where: JSON.stringify([{ attribute: "owner_type", value: "system" }]),
  };

  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: ["driverCompanies", params],
    queryFn: async () => {
      const response = await apiClient.get(
        `/driver_companies${queryString ? `?${queryString}` : ""}`,
      );
      return {
        driverCompanies: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Hook to toggle driver company enabled status
 * @returns {Object} Mutation object
 */
export const useToggleDriverCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ driverId, enabled }) => {
      const response = await apiClient.post(`/driver_companies/${driverId}`, {
        enabled,
      });
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ["driverCompanies"] });
      // Also invalidate all driverGroup queries to update the detail page
      queryClient.invalidateQueries({ queryKey: ["driverGroup"] });

      toast.success(
        variables.enabled
          ? "Delivery service enabled successfully"
          : "Delivery service disabled successfully",
      );
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update delivery service");
    },
  });
};

/**
 * Hook to install a plugin
 * @returns {Object} Mutation object
 */
export const useInstallPlugin = () => {
  return useMutation({
    mutationFn: async ({ pluginRoot }) => {
      const response = await apiClient.post("/plugins", { url: pluginRoot });
      return response;
    },
    onSuccess: () => {
      toast.success("Plugin installed successfully");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to install plugin");
    },
  });
};

/**
 * Hook to update driver group settings
 * @returns {Object} Mutation object
 */
export const useUpdateDriverGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ driverGroupId, data }) => {
      const response = await apiClient.put(
        `/drivergroups/${driverGroupId}`,
        data,
      );
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate driver group queries
      queryClient.invalidateQueries({
        queryKey: ["driverGroup", variables.driverGroupId],
      });
      queryClient.invalidateQueries({ queryKey: ["driverGroups"] });

      toast.success("Driver group updated successfully");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update driver group");
    },
  });
};

/**
 * Hook to get driver group detail by ID
 * @param {number} driverGroupId - The driver group ID
 * @returns {Object} Query result with driver group details
 */
export const useDriverGroupDetail = (driverGroupId) => {
  const project = useApiStore((state) => state.project);

  return useQuery({
    queryKey: ["driverGroup", driverGroupId],
    queryFn: async () => {
      const response = await apiClient.get(`/drivergroups/${driverGroupId}`);
      const driverGroup = response?.result || {};
      const driverCompany = driverGroup?.driver_companies?.[0] || {};

      return {
        driverGroup,
        driverCompany,
        project,
      };
    },
    enabled: !!driverGroupId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Get the setup modal title based on plugin key
 * @param {string} pluginKey - The plugin key (e.g., 'doordash_drive_integration')
 * @returns {string} The title for the setup modal
 */
export const getSetupModalTitle = (pluginKey) => {
  switch (pluginKey) {
    case "doordash_drive_integration":
      return "Setting up on Doordash";
    case "uber_direct_integration":
      return "Setting up on Uber Direct";
    case "yango_delivery_integration":
      return "Setting up on Yango Delivery";
    default:
      return "Setting up";
  }
};

/**
 * Build the plugin setup URL from plugin data
 * @param {Object} plugin - The plugin object
 * @param {string} project - The project code
 * @returns {string|null} The full URL for the plugin setup iframe
 */
export const buildPluginSetupUrl = (plugin, project) => {
  if (!plugin?.root || !plugin?.links) return null;

  const links =
    typeof plugin.links === "string"
      ? JSON.parse(plugin.links || "{}")
      : plugin.links || {};

  const infoSetup = links?.info_setup?.replace("@{project_code}", project);

  if (!infoSetup) return null;

  return `${plugin.root}${infoSetup}`;
};

export default {
  useDriverCompanies,
  useToggleDriverCompany,
  useInstallPlugin,
  useDriverGroupDetail,
  useUpdateDriverGroup,
  getSetupModalTitle,
  buildPluginSetupUrl,
};
